<template>
  <div class="page-container animate-fade-in">
    <PageHeader
      title="Page Visits"
      description="Visitor analytics stored in Firebase Realtime Database."
      :breadcrumbs="[{ label: 'Admin', to: '/admin' }, { label: 'Analytics' }]"
    >
      <template #actions>
        <BaseButton variant="secondary" :loading="loading" @click="loadAnalytics">
          <AppIcon name="RefreshCw" :size="16" />
          Refresh
        </BaseButton>
      </template>
    </PageHeader>

    <div v-if="loading" class="glass-card p-8 text-center text-muted">Loading analytics...</div>

    <template v-else>
      <div class="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div v-for="stat in summaryCards" :key="stat.label" class="glass-card p-5">
          <p class="text-2xl font-semibold text-foreground">{{ stat.value }}</p>
          <p class="text-sm text-muted mt-1">{{ stat.label }}</p>
        </div>
      </div>

      <div class="grid lg:grid-cols-2 gap-6 mb-6">
        <BaseCard title="Visits by page" description="All-time page and section views">
          <div v-if="!pageRows.length" class="text-sm text-muted py-4">No page data yet.</div>
          <div v-else class="mt-4 space-y-3">
            <div v-for="row in pageRows" :key="row.page" class="space-y-1.5">
              <div class="flex items-center justify-between text-sm">
                <span class="font-medium text-foreground capitalize">{{ row.page }}</span>
                <span class="text-muted">{{ row.count }}</span>
              </div>
              <div class="h-2 rounded-full bg-border-subtle overflow-hidden">
                <div
                  class="h-full rounded-full bg-primary transition-all"
                  :style="{ width: `${row.percent}%` }"
                />
              </div>
            </div>
          </div>
        </BaseCard>

        <BaseCard title="Last 14 days" description="Daily visit totals">
          <div v-if="!dailyRows.length" class="text-sm text-muted py-4">No daily data yet.</div>
          <div v-else class="mt-4 space-y-2">
            <div
              v-for="day in dailyRows"
              :key="day.date"
              class="flex items-center gap-3 text-sm"
            >
              <span class="w-24 shrink-0 text-muted">{{ formatDay(day.date) }}</span>
              <div class="flex-1 h-2 rounded-full bg-border-subtle overflow-hidden">
                <div
                  class="h-full rounded-full bg-primary/80"
                  :style="{ width: `${day.percent}%` }"
                />
              </div>
              <span class="w-8 text-right text-foreground">{{ day.count }}</span>
            </div>
          </div>
        </BaseCard>
      </div>

      <BaseCard title="Recent visits" description="Latest tracked page views">
        <div v-if="!recentVisits.length" class="text-sm text-muted py-4">No visits recorded yet.</div>
        <div v-else class="mt-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-muted border-b border-border-subtle">
                <th class="py-2 pr-4 font-medium">When</th>
                <th class="py-2 pr-4 font-medium">Page</th>
                <th class="py-2 pr-4 font-medium">Path</th>
                <th class="py-2 font-medium">Referrer</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="visit in recentVisits"
                :key="visit.id"
                class="border-b border-border-subtle/60 last:border-0"
              >
                <td class="py-3 pr-4 text-muted whitespace-nowrap">{{ formatWhen(visit.at) }}</td>
                <td class="py-3 pr-4 capitalize text-foreground">{{ visit.page }}</td>
                <td class="py-3 pr-4 text-muted">{{ visit.path }}</td>
                <td class="py-3 text-muted truncate max-w-[12rem]" :title="visit.referrer">
                  {{ visit.referrer || 'Direct' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </BaseCard>

      <p class="text-xs text-muted mt-4">
        Firebase Console Analytics may show additional Google Analytics data. This dashboard reads visit counters stored in
        <code class="text-primary">analytics/</code> on each page view.
      </p>
    </template>
  </div>
</template>

<script>
import {
  fetchAnalytics,
  sumDailyRange,
  visitsToday,
} from '@/services/pageAnalytics'
import PageHeader from '@/components/layout/PageHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

export default {
  name: 'AdminAnalytics',
  components: { PageHeader, BaseCard, BaseButton, AppIcon },
  data() {
    return {
      loading: true,
      total: 0,
      pages: {},
      daily: {},
      recentVisits: [],
    }
  },
  computed: {
    summaryCards() {
      const weekTotal = sumDailyRange(this.daily, 7).reduce((sum, day) => sum + day.count, 0)

      return [
        { label: 'Total visits', value: this.total },
        { label: 'Today', value: visitsToday(this.daily) },
        { label: 'Last 7 days', value: weekTotal },
        { label: 'Tracked pages', value: Object.keys(this.pages).length },
      ]
    },
    pageRows() {
      const max = Math.max(...Object.values(this.pages).map(Number), 1)

      return Object.entries(this.pages)
        .map(([page, count]) => ({
          page,
          count: Number(count) || 0,
          percent: ((Number(count) || 0) / max) * 100,
        }))
        .sort((a, b) => b.count - a.count)
    },
    dailyRows() {
      const rows = sumDailyRange(this.daily, 14)
      const max = Math.max(...rows.map((row) => row.count), 1)

      return rows.map((row) => ({
        ...row,
        percent: (row.count / max) * 100,
      }))
    },
  },
  async created() {
    await this.loadAnalytics()
  },
  methods: {
    async loadAnalytics() {
      this.loading = true
      try {
        const data = await fetchAnalytics()
        this.total = data.total
        this.pages = data.pages
        this.daily = data.daily
        this.recentVisits = data.recent.slice(0, 50)
      } finally {
        this.loading = false
      }
    },
    formatWhen(timestamp) {
      if (!timestamp) return 'Unknown'
      return new Date(timestamp).toLocaleString()
    },
    formatDay(dateKey) {
      const date = new Date(`${dateKey}T00:00:00`)
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
  },
}
</script>
